<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard">
        <template v-slot:header>Run check now</template>
        <template v-slot:subheader>{{ props.teamName }}</template>

        <div class="flex flex-col gap-4 text-sm text-slate-800">
            <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
                <dt class="font-bold">Team</dt>
                <dd class="m-0" data-test="start-team">{{ props.teamId }} - {{ props.teamName }}</dd>
                <dt class="font-bold">RND Company ID</dt>
                <dd class="m-0 font-mono" data-test="start-company-id">{{ props.companyId }}</dd>
                <dt class="font-bold">Marketing subscribers</dt>
                <dd class="m-0" data-test="start-subscribers">{{ countLabel(props.subscriberCount) }}</dd>
            </dl>

            <p class="m-0" data-test="start-billing">
                Each marketing subscriber is queried at most once, so this run sends up to
                {{ countLabel(props.subscriberCount) }} queries, each billed to Voxie. Reassigned contacts cost a few
                more, for the follow-up query that tells whether the new holder has already opted in or out.
            </p>

            <p class="m-0" data-test="start-reset">
                Contacts with reassigned numbers are reset: their name, email, tags, custom attributes and location
                memberships are cleared, they leave campaigns and workflows, and both their subscriptions are set to no
                preference. A contact that has opted in or out since its number changed hands is left alone. A reset
                cannot be undone.
            </p>

            <div class="flex flex-col gap-1">
                <vx-checkbox
                    v-model="requiresApproval"
                    data-test="review-resets"
                    :aria-describedby="reviewDescriptionId"
                >
                    Review resets before applying
                </vx-checkbox>
                <p :id="reviewDescriptionId" class="m-0 pl-6 text-slate-500" data-test="review-resets-description">
                    {{ requiresApproval ? REVIEW_ON_TEXT : REVIEW_OFF_TEXT }}
                </p>
            </div>

            <vx-alert v-if="supersedesText !== null" color="danger" :closeable="false" data-test="start-supersedes">
                {{ supersedesText }}
            </vx-alert>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-start"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Start check
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxAlert, VxButton, VxCheckbox, VxModal } from '@voxie/frontend-components';
import { isAxiosError } from 'axios';
import { computed, ref, useId, watch } from 'vue';
import {
    REVIEW_OFF_TEXT,
    REVIEW_ON_TEXT,
    canReviewRun,
    countLabel,
    runStatusLabels,
} from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import { useToasted } from '~/composables/useToasted';
import { adminRndClient } from '~/services';
import type { RndRun } from '~/services/adminRndClient.types';
import { HTTP_CONFLICT } from '~/support/HttpStatuses';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{
    teamId: number;
    teamName: string;
    companyId: string;
    subscriberCount: number;
    supersededRun: RndRun | null;
}>();
const emit = defineEmits<{ started: [run: RndRun]; conflict: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();

const reviewDescriptionId = useId();

const submitting = ref(false);
const requiresApproval = ref(false);

const supersedesText = computed(() => {
    const run = props.supersededRun;

    if (run === null) {
        return null;
    }

    return canReviewRun(run)
        ? `This run supersedes run #${run.id}, which is awaiting approval, so none of its resets are ever applied.`
        : `This run supersedes run #${run.id} (${
              runStatusLabels[run.status]
          }), so it can no longer be followed up with Check remaining contacts, and any reset it decided but did not apply is never applied.`;
});

const confirm = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const { data } = await adminRndClient.createRun({
            team_id: props.teamId,
            requires_approval: requiresApproval.value,
        });

        emit('started', data);
        visible.value = false;
    } catch (error) {
        toasted.error(catchRequestError(error), { sticky: true });

        if (isAxiosError(error) && HTTP_CONFLICT === error.response?.status) {
            emit('conflict');
            visible.value = false;
        }
    } finally {
        submitting.value = false;
    }
};

watch(visible, (open) => {
    if (open) {
        requiresApproval.value = false;
    }
});
</script>
